import { useEffect, useRef } from 'react';

// モーダル/オーバーレイを「ブラウザの戻る・戻るジェスチャー」で閉じるための共有機構。
//
// 仕組み（既存のチャット画像モーダルと同じ実績パターンを一般化）:
//  - 開いている間、同一URLのダミー履歴エントリを1つ積む（pushState）。
//  - 戻る操作＝popstate で最前面の層の onClose を呼ぶ（＝モーダルを閉じる。ページ遷移はしない）。
//  - ボタン/Esc/背景などプログラム的に閉じたら、積んだエントリを history.back() で消費して整合を保つ。
//  - 入れ子モーダルはスタックで管理し、常に最前面から閉じる（LIFO）。
//
// ページ側にも独自の popstate ハンドラ（チャットの mobile list⇔chat 遷移など）が存在するため、
// このリスナーは capture フェーズで登録し、「モーダル層が戻るを処理したとき」だけ
// stopImmediatePropagation() でページ側／react-router の popstate ハンドラへの波及を止める。
// 閉じる層が無いときは何もせず、ページ側ハンドラに委ねる。
// ダミーエントリは同一URL＋既存 history.state を保持して push するため、react-router の
// location/idx は不変で、波及を止めても整合が保たれる。

const layerStack = [];
// 自前の後始末 back は同時に複数走り得る（入れ子モーダルを続けて閉じたとき）。
// 単一フラグだと2回目の popstate を取りこぼし、下の層まで閉じて履歴も戻り過ぎるため件数で持つ。
let pendingSelfPops = 0;
let listenerAttached = false;

function handlePopState(event) {
  // 自前の後始末 back（removeLayer）による popstate は、ページ側へ波及させずに握りつぶす。
  if (pendingSelfPops > 0) {
    pendingSelfPops -= 1;
    event.stopImmediatePropagation();
    return;
  }
  if (layerStack.length === 0) return; // 閉じる層なし → ページ側ハンドラに委ねる。
  // モーダルを閉じる戻る操作は、ページ側の履歴ハンドラを二重発火させないよう波及を止める。
  event.stopImmediatePropagation();
  const layer = layerStack.pop();
  // ブラウザは既に push 済みエントリを pop している。React 側の onClose を呼ぶだけ。
  layer.onClose();
}

function ensureListener() {
  if (listenerAttached || typeof window === 'undefined') return;
  // capture: ページ側の bubble フェーズ popstate ハンドラより先に判定する。
  window.addEventListener('popstate', handlePopState, true);
  listenerAttached = true;
}

function pushLayer(layer) {
  ensureListener();
  try {
    const base =
      window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
    // 同一URLで push（第3引数省略）。back で元エントリに戻る＝URL不変＝ルート遷移しない。
    window.history.pushState({ ...base, __dismissLayer: layerStack.length + 1 }, '');
  } catch {
    // pushState 不可でも致命ではない（戻るで閉じないだけ）。
  }
  layerStack.push(layer);
}

function removeLayer(layer) {
  const index = layerStack.lastIndexOf(layer);
  if (index === -1) return; // 戻る操作で既に pop 済み。
  const wasTop = index === layerStack.length - 1;
  layerStack.splice(index, 1);
  // 自分が積んだエントリが最前面のときだけ history を巻き戻して整合させる。
  if (wasTop) {
    pendingSelfPops += 1;
    try {
      window.history.back();
    } catch {
      pendingSelfPops -= 1;
    }
  }
}

/**
 * @param {boolean} active   開いている（かつ戻るで閉じてよい）とき true。
 * @param {() => void} onClose  戻る操作で呼ぶクローズ関数。
 */
export default function useHistoryDismiss(active, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return undefined;
    const layer = { onClose: () => onCloseRef.current?.() };
    pushLayer(layer);
    return () => removeLayer(layer);
  }, [active]);
}
